/**
 * Buffer polyfill MUST be the very first thing executed.
 * Some Solana/Anchor dependencies access Buffer at module load time,
 * so it must exist on window before any other imports are evaluated.
 * The vite-plugin-node-polyfills also injects Buffer globally, but
 * this serves as a defensive fallback.
 */
import { Buffer } from 'buffer'
if (typeof window !== 'undefined' && !window.Buffer) {
  window.Buffer = Buffer
}

import React, { useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react'
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base'
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui'
import { clusterApiUrl } from '@solana/web3.js'
import { Toaster } from 'react-hot-toast'
import {
  SolanaMobileWalletAdapter,
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
  createDefaultWalletNotFoundHandler,
} from '@solana-mobile/wallet-adapter-mobile'

import App from './App'
import './styles/globals.css'
import debugLogger from './lib/debugLogger'
import ErrorBoundary from './components/ErrorBoundary'
import { TermsProvider } from './components/TermsProvider'

// Import required CSS for wallet adapter UI components
import '@solana/wallet-adapter-react-ui/styles.css'

/**
 * Main application entry point that sets up the React app with Solana wallet providers.
 * Configures wallet adapters for connecting to Phantom, Solflare and other wallets,
 * including mobile wallets via the Solana Mobile Wallet Adapter (MWA) protocol.
 */

// Configure the Solana network (can be 'mainnet-beta', 'testnet', or 'devnet')
const network = WalletAdapterNetwork.Devnet

// Get the RPC endpoint for the selected network
// You can replace this with a custom RPC endpoint for better performance
const endpoint = clusterApiUrl(network)

/**
 * Detects whether the current browser is running on a mobile device.
 * Used to conditionally configure mobile wallet adapter support.
 * Checks user agent string and touch capability for comprehensive detection.
 */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }
  const userAgent = navigator.userAgent || navigator.vendor || ''
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  return mobileRegex.test(userAgent) || (hasTouchScreen && window.innerWidth < 768)
}

/**
 * Returns the base URI for the app, used for mobile wallet adapter app identity.
 * On mobile, this tells the wallet app where to redirect back to after signing.
 */
function getAppUri(): string {
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}`
  }
  return 'https://ctopools.com'
}

/**
 * Custom toast configuration for better user experience.
 * Shows notifications for wallet connections, transactions, errors, etc.
 */
const toastOptions = {
  duration: 4000,
  position: 'bottom-right' as const,
  style: {
    background: '#0c101c',
    color: '#ffffff',
    border: '1px solid #00E0FF',
    borderRadius: '0.375rem',
    boxShadow: '0 0 10px rgba(0, 224, 255, 0.3)'
  },
  success: {
    iconTheme: {
      primary: '#00FF88',
      secondary: '#0c101c'
    }
  },
  error: {
    iconTheme: {
      primary: '#FF0080',
      secondary: '#0c101c'
    }
  }
}

/**
 * Main App Component with Providers.
 * Wraps the entire application with necessary context providers for:
 * - Solana connection management (ConnectionProvider)
 * - Wallet adapter functionality (WalletProvider) — includes mobile wallet adapter
 * - Wallet modal UI (WalletModalProvider)
 * - Terms of Service gate (TermsProvider)
 * - React routing (BrowserRouter)
 * - Toast notifications (Toaster)
 */
function MainApp() {
  debugLogger.info('MainApp', '🌟 Starting application initialization')
  debugLogger.timeStart('MainApp', 'initialization')

  // Use different basename for development vs production (GitHub Pages)
  const basename = process.env.NODE_ENV === 'production' ? '/cto-pools' : '/'
  debugLogger.info('MainApp', '🔍 Using basename', { basename, nodeEnv: process.env.NODE_ENV })

  /**
   * Wallet adapters configuration.
   * 
   * Desktop wallets (Phantom, Solflare, Backpack, etc.) implement the
   * Wallet Standard and are automatically detected by WalletProvider —
   * no manual adapter is needed for them.
   * 
   * Mobile wallets use the Solana Mobile Wallet Adapter (MWA) protocol,
   * which requires explicit adapter registration. The SolanaMobileWalletAdapter
   * handles deep link URL schemes (phantom://, solflare://, etc.) and the
   * MWA communication protocol for transaction signing on mobile devices.
   * 
   * The adapter is memoized to prevent re-instantiation on every render.
   */
  const wallets = useMemo(() => {
    const adapters = []

    // Add Mobile Wallet Adapter for mobile device support
    // This adapter enables deep-link based wallet connections on mobile browsers
    // It handles the MWA protocol: opening wallet apps, signing, and returning to the dApp
    if (isMobileDevice()) {
      debugLogger.info('MainApp', '📱 Mobile device detected — registering MWA adapter')
      adapters.push(
        new SolanaMobileWalletAdapter({
          // Selects which address to use when the wallet authorizes multiple accounts
          addressSelector: createDefaultAddressSelector(),
          // Identifies this dApp to the mobile wallet (shown in wallet's authorization prompt)
          appIdentity: {
            name: 'CTO Pools',
            uri: getAppUri(),
            icon: '/favicon.svg',
          },
          // Caches authorization tokens so the user doesn't have to re-approve on every visit
          authorizationResultCache: createDefaultAuthorizationResultCache(),
          // Must match the network the RPC endpoint connects to
          cluster: network,
          // When no compatible wallet app is installed, this handler shows an install prompt
          onWalletNotFound: createDefaultWalletNotFoundHandler(),
        })
      )
    } else {
      debugLogger.info('MainApp', '🖥️ Desktop detected — relying on Wallet Standard auto-detection')
    }

    return adapters
  }, [])

  debugLogger.info('MainApp', '🔗 Initializing Solana providers', {
    endpoint,
    walletCount: wallets.length,
    network,
    isMobile: isMobileDevice()
  })

  try {
    debugLogger.info('MainApp', '✅ Rendering provider tree')
    debugLogger.timeEnd('MainApp', 'initialization')

    return (
      <ErrorBoundary>
        <ConnectionProvider endpoint={endpoint}>
          <WalletProvider wallets={wallets} autoConnect>
            <WalletModalProvider>
              {/* TermsProvider intercepts wallet connections and prompts users to accept Terms of Service */}
              <TermsProvider>
                <BrowserRouter basename={basename}>
                  <App />
                  <Toaster
                    toastOptions={toastOptions}
                    containerStyle={{
                      top: 20,
                      left: 20,
                      bottom: 20,
                      right: 20,
                    }}
                  />
                </BrowserRouter>
              </TermsProvider>
            </WalletModalProvider>
          </WalletProvider>
        </ConnectionProvider>
      </ErrorBoundary>
    )
  } catch (error) {
    debugLogger.error('MainApp', '❌ Critical error in provider setup', error)
    debugLogger.lifecycle('MainApp', 'error', { error })

    // Minimal fallback UI when providers fail to initialize
    return (
      <div className="min-h-screen bg-gray-900 text-white p-8">
        <h1 className="text-2xl mb-4">Application Error</h1>
        <p>Failed to initialize application providers.</p>
        <pre className="text-sm bg-gray-800 p-4 mt-4 rounded">
          {error instanceof Error ? error.message : String(error)}
        </pre>
      </div>
    )
  }
}

/**
 * Initialize and render the React application.
 * Ensures the DOM is ready before mounting the app.
 */
debugLogger.info('main.tsx', '🏁 Starting React application')
debugLogger.timeStart('main.tsx', 'app-render')

const rootElement = document.getElementById('root')
debugLogger.debug('main.tsx', '🔍 Root element check', { found: !!rootElement })

if (!rootElement) {
  debugLogger.error('main.tsx', '❌ Root element not found')
  throw new Error('Failed to find the root element. Make sure there is a div with id="root" in your HTML.')
}

debugLogger.debug('main.tsx', '🔍 Creating React root')
const root = ReactDOM.createRoot(rootElement)

debugLogger.debug('main.tsx', '🔍 Rendering React app')

// Add global error handlers before rendering
window.addEventListener('error', (event) => {
  debugLogger.error('main.tsx', '🚨 Global Error Event', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error
  })
})

window.addEventListener('unhandledrejection', (event) => {
  debugLogger.error('main.tsx', '🚨 Unhandled Promise Rejection', {
    reason: event.reason
  })
})

try {
  // Enable React Strict Mode for development warnings and better debugging
  root.render(
    <React.StrictMode>
      <MainApp />
    </React.StrictMode>
  )
  debugLogger.timeEnd('main.tsx', 'app-render')
  debugLogger.info('main.tsx', '✅ React app rendered successfully')
} catch (renderError) {
  debugLogger.error('main.tsx', '❌ Failed to render React app', renderError)
  debugLogger.lifecycle('main.tsx', 'error', { renderError })
}

// Hot reload support for development
// @ts-expect-error - Vite HMR types
if (import.meta.hot) {
  // @ts-expect-error - Vite HMR types
  import.meta.hot.accept()
}

debugLogger.info('main.tsx', '✅ Application bootstrap complete')
